import { refsIn } from "@minga/core"
import { type Notification, notificationPath, type RefView } from "@minga/protocol"
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import { type ReactNode, useCallback, useMemo, useState } from "react"
import { markNotificationsRead, notificationsCountQuery, notificationsQuery } from "@/lib/notifications-api"
import { answerRecommendedHttp, refViewsQuery } from "@/lib/refs-api"
import { cn } from "@/lib/utils"
import { projectsQuery } from "@/lib/workspace-api"
import { INBOX_LINES, whenText } from "@/model/glossary"
import { RefText } from "./markdown/ref-text"
import { RefContext, RefLookupContext, type RefResolver, refTone } from "./markdown/refs"
import { Glyph } from "./refs/ref-icons"
import { Button } from "./ui/button"
import { Popover } from "./ui/popover"

/** Decision 0113: the same link a push notification opens (`notificationPath` in the protocol). */
export const notificationHref = (notice: Notification): string => notificationPath(notice)

/** Outcomes and person-needed decisions, using the app's existing responsive popover/sheet. */
export function NotificationInboxButton({ workspaceId }: { workspaceId: string }) {
  const [open, setOpen] = useState(false)
  const [offset, setOffset] = useState(0)
  // the rows load when the bell opens; every page asks only for the count
  const query = useQuery({ ...notificationsQuery(workspaceId, offset), enabled: open })
  const unread = useQuery(notificationsCountQuery(workspaceId)).data ?? 0
  const client = useQueryClient()
  const router = useRouter()
  const read = useMutation({
    mutationFn: markNotificationsRead,
    onSuccess: () => client.invalidateQueries({ queryKey: ["me", "notifications", workspaceId] }),
  })
  const rows = query.data?.notifications ?? []
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      title="Notifications"
      role="dialog"
      className="w-[min(24rem,calc(100vw-2rem))]"
      trigger={(props) => (
        <button
          {...props}
          type="button"
          className="tap flex h-9 items-center gap-1 rounded-control px-2 text-muted hover:bg-fill hover:text-ink"
          aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            aria-hidden="true"
          >
            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9ZM10 21h4" />
          </svg>
          {unread > 0 && <span className="count-badge">{unread}</span>}
        </button>
      )}
    >
      <div className="flex items-center justify-between gap-2 p-2">
        <h2 className="font-semibold">Notifications</h2>
        <button
          type="button"
          className="tap text-sm"
          disabled={read.isPending || !rows.some((r) => !r.seenAt)}
          onClick={() => read.mutate(rows.filter((r) => !r.seenAt).map((r) => r.id))}
        >
          Mark page read
        </button>
      </div>
      <p className="type-meta m-0 px-2" data-inbox-line="">
        {INBOX_LINES.bell}
      </p>
      <div className="max-h-[60dvh] overflow-y-auto p-2">
        {query.isPending && (
          <p role="status" className="type-meta">
            Loading notifications…
          </p>
        )}
        {query.isError && (
          <div role="alert">
            <p>Notifications could not load.</p>
            <button type="button" className="tap" onClick={() => void query.refetch()}>
              Retry
            </button>
          </div>
        )}
        {!query.isPending && !query.isError && !rows.length && (
          <p className="type-meta">
            No notifications here. Completed work and decisions needing you will appear here.
          </p>
        )}
        {/* decision 0144: rows in the card shape, refs as chips resolved in each row's own project */}
        <BellRows
          workspaceId={workspaceId}
          rows={rows}
          readPending={read.isPending}
          onRead={(ids) => read.mutate(ids)}
          onOpen={(r) => {
            read.mutate([r.id])
            setOpen(false)
            router.history.push(notificationHref(r))
          }}
          onAnswered={() => client.invalidateQueries({ queryKey: ["me", "notifications", workspaceId] })}
        />
        {read.isError && (
          <p role="alert" className="text-needs">
            Could not mark read. Try again.
          </p>
        )}
      </div>
      <div className="flex justify-between p-2">
        {offset > 0 && (
          <button type="button" className="tap" onClick={() => setOffset(Math.max(0, offset - 50))}>
            Newer
          </button>
        )}
        {query.data?.nextOffset !== null && query.data?.nextOffset !== undefined && (
          <button
            type="button"
            className="tap ml-auto"
            onClick={() => {
              if (query.data?.nextOffset != null) setOffset(query.data.nextOffset)
            }}
          >
            Older
          </button>
        )}
      </div>
    </Popover>
  )
}

/** The row's icon: a card (blue when it waits on you), an item, a brief, the rest a bell-less dot. */
const rowIcon = (r: Notification): { name: string; needs: boolean } =>
  r.source._tag === "proposal"
    ? { name: "card", needs: !r.doneAt }
    : r.source._tag === "item"
      ? { name: "task", needs: false }
      : r.source._tag === "digest"
        ? { name: "output", needs: false }
        : r.source._tag === "runner"
          ? { name: "run", needs: false }
          : { name: "reply", needs: false }

/**
 * Decision 0144 (0136 §3, wording rules 3 and 7): one bell row per notification in the shape: "project · the ask or
 * the news" as the title, one line for the recommendation or the result, the recommended answer as a button when the
 * card may be answered from a notification, and Open. Refs in the line are chips resolved in the row's own project
 * (one lookup per project), never in the page's project.
 */
function BellRows({
  workspaceId,
  rows,
  readPending,
  onRead,
  onOpen,
  onAnswered,
}: {
  workspaceId: string
  rows: ReadonlyArray<Notification>
  readPending: boolean
  onRead: (ids: ReadonlyArray<string>) => void
  onOpen: (r: Notification) => void
  onAnswered: () => void
}) {
  const projects = useQuery(projectsQuery(workspaceId))
  const names = new Map((projects.data ?? []).map((p) => [p.id as string, p.name]))
  const groups = [...new Set(rows.map((r) => r.projectId ?? ""))].filter((p) => p !== "")
  const refsOf = (projectId: string) =>
    rows.filter((r) => r.projectId === projectId).flatMap((r) => refsIn(`${r.title}\n${r.reason}`))
  const lookups = useQueries({ queries: groups.map((pid) => refViewsQuery(workspaceId, pid, refsOf(pid))) })
  const views = new Map(
    groups.map((pid, i) => [pid, new Map((lookups[i]?.data ?? []).map((v) => [v.ref, v] as const))] as const),
  )
  return (
    <>
      {rows.map((r) => {
        const pid = r.projectId ?? ""
        const projectViews = views.get(pid) ?? new Map<string, RefView>()
        return (
          <BellRowProviders key={r.id} workspaceId={workspaceId} projectId={pid} views={projectViews}>
            <BellRow
              row={r}
              workspaceId={workspaceId}
              projectName={r.projectName ?? names.get(pid) ?? ""}
              readPending={readPending}
              onRead={onRead}
              onOpen={onOpen}
              onAnswered={onAnswered}
            />
          </BellRowProviders>
        )
      })}
    </>
  )
}

function BellRowProviders({
  workspaceId,
  projectId,
  views,
  children,
}: {
  workspaceId: string
  projectId: string
  views: ReadonlyMap<string, RefView>
  children: ReactNode
}) {
  const resolve: RefResolver = useCallback(
    (ref) => {
      const view = views.get(ref)
      return view ? { tone: refTone(ref), title: view.title, view } : null
    },
    [views],
  )
  const lookup = useMemo(() => (projectId ? { workspaceId, projectId } : null), [workspaceId, projectId])
  return (
    <RefLookupContext.Provider value={lookup}>
      <RefContext.Provider value={resolve}>{children}</RefContext.Provider>
    </RefLookupContext.Provider>
  )
}

function BellRow({
  row: r,
  workspaceId,
  projectName,
  readPending,
  onRead,
  onOpen,
  onAnswered,
}: {
  row: Notification
  workspaceId: string
  projectName: string
  readPending: boolean
  onRead: (ids: ReadonlyArray<string>) => void
  onOpen: (r: Notification) => void
  onAnswered: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [answered, setAnswered] = useState(false)
  const icon = rowIcon(r)
  const answer =
    r.answer && r.actionableFromNotification && !r.doneAt && r.source._tag === "proposal" && r.projectId
      ? r.answer
      : null
  const send = async () => {
    if (!answer || r.source._tag !== "proposal" || !r.projectId) return
    setBusy(true)
    setError(null)
    try {
      await answerRecommendedHttp(workspaceId, r.projectId, r.source.proposalId, answer.revision)
      setAnswered(true)
      onRead([r.id])
      onAnswered()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <article
      data-bell-row={r.source._tag}
      className="grid min-w-0 grid-cols-[22px_1fr_auto] gap-x-2.5 gap-y-0.5 border-b border-rule py-3"
    >
      <span className={cn("pt-0.5", icon.needs ? "text-needs" : "text-ink-2")}>
        <Glyph name={icon.name} size={14} />
      </span>
      <a
        href={notificationHref(r)}
        className="min-w-0 text-[14px] leading-snug font-semibold break-words text-ink no-underline hover:underline"
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
          event.preventDefault()
          onOpen(r)
        }}
      >
        {!r.seenAt && (
          <span
            role="img"
            aria-label="unread"
            className="mr-1.5 mb-0.5 inline-block size-1.5 rounded-full bg-needs align-middle"
          />
        )}
        {projectName ? `${projectName} · ` : ""}
        <RefText text={r.title} plain />
      </a>
      <span className="type-meta whitespace-nowrap">{whenText(r.createdAt, Date.now())}</span>
      {r.reason && (
        <p className="col-start-2 col-end-4 m-0 line-clamp-2 text-[13px] leading-[1.4] break-words text-ink-2">
          <RefText text={r.reason.split("\n").find((l) => l.trim() !== "") ?? ""} />
        </p>
      )}
      <div className="col-start-2 col-end-4 mt-1.5 flex flex-wrap items-center gap-1.5">
        {answer && !answered && (
          <Button size="sm" variant="decision" disabled={busy} onClick={() => void send()}>
            {answer.label}
          </Button>
        )}
        {answered && (
          <span role="status" className="text-[13px] font-semibold text-live">
            {answer?.label}: sent
          </span>
        )}
        <Button size="sm" variant="secondary" onClick={() => onOpen(r)}>
          Open
        </Button>
        <span className="type-meta ml-auto flex items-center gap-2">
          {r.doneAt ? "resolved" : r.seenAt ? "read" : ""}
          {!r.seenAt && (
            <button type="button" className="tap" disabled={readPending} onClick={() => onRead([r.id])}>
              Mark read
            </button>
          )}
        </span>
      </div>
      {error && (
        <p role="alert" className="col-start-2 col-end-4 m-0 text-[13px] text-blocked">
          {error}
        </p>
      )}
    </article>
  )
}
